/* eslint-disable no-nested-ternary */
/* eslint-disable jsdoc/require-jsdoc */
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../controls/Button';
import { getCheckoutDevices, getPaypalSubscription } from '../services/dashboard-service';
import { useData } from '../context/data.context';

type PaymentStatus = 'processing' | 'success' | 'error';

/**
 * PayPal checkout success page.
 * Handles redirect back from PayPal, confirms payment with backend, and refreshes user data.
 * @returns React component for success state.
 */
export default function PaypalSuccessPage(): ReactElement {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useData();
  const [status, setStatus] = useState<PaymentStatus>('processing');
  const [message, setMessage] = useState('Confirming your payment. Please wait…');
  const [planName, setPlanName] = useState<string>('your plan');
  const [refreshWarning, setRefreshWarning] = useState<string | null>(null);
  const hasProcessedRef = useRef<number | null>(null);
  const isProcessingRef = useRef<boolean>(false);

  const subscriptionId = useMemo(() => {
    const param = searchParams.get('subscriptionId');
    if (!param) return null;
    const parsed = parseInt(param, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }, [searchParams]);

  useEffect(() => {
    // Skip if no subscription ID or already processing/completed
    if (!subscriptionId || hasProcessedRef.current === subscriptionId || isProcessingRef.current) {
      return undefined;
    }

    let isMounted = true;
    isProcessingRef.current = true;

    const finalizePayment = async (): Promise<void> => {
      try {
        const response = await getPaypalSubscription(subscriptionId);
        const subscription = response.data;

        if (isMounted) {
          setPlanName(subscription.planName);
        }

        if (subscription.status !== 'SUCCESS') {
          if (isMounted) {
            setStatus('error');
            setMessage(
              `Your subscription status is ${subscription.status}. Please contact support if you believe this is an error.`
            );
          }
          hasProcessedRef.current = subscriptionId;
          isProcessingRef.current = false;
          return;
        }

        // Mark as processed before setting status to prevent re-triggering
        hasProcessedRef.current = subscriptionId;
        isProcessingRef.current = false;

        // A checkout covers one or more devices
        let deviceCount = 1;
        try {
          const devicesResponse = await getCheckoutDevices(subscriptionId);
          deviceCount = devicesResponse.data.filter((s) => s.status === 'SUCCESS').length || 1;
        } catch {
          // The count is only for the message; the plan is already active.
        }
        const devicesText = `${deviceCount} device${deviceCount !== 1 ? 's' : ''}`;

        // Set success status immediately using functional update to ensure it persists
        setStatus(() => 'success');
        setMessage(`All set! ${devicesText} ${deviceCount !== 1 ? 'are' : 'is'} now on the ${subscription.planName} plan.`);

        // Refresh data in background (don't block UI) - use setTimeout to ensure status update happens first
        setTimeout(async () => {
          try {
            await refresh();
          } catch (refreshError) {
            // eslint-disable-next-line no-console
            console.error('Failed to refresh data after PayPal success', refreshError);
            setRefreshWarning(
              'Your payment was confirmed, but we could not refresh your account automatically. You may need to reload the dashboard.'
            );
          }
        }, 100);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Unable to fetch PayPal subscription details', error);
        isProcessingRef.current = false;
        // Reset the processed ref on error so user can retry
        hasProcessedRef.current = null;
        if (isMounted) {
          setStatus('error');
          setMessage(
            'We could not confirm your payment. No charges have been applied. Please try again or contact support.'
          );
        }
      }
    };

    finalizePayment();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionId]);

  const heading =
    status === 'success'
      ? 'Payment Confirmed'
      : status === 'error'
      ? 'Something Went Wrong'
      : 'Processing Payment';

  const icon =
    status === 'success' ? 'check_circle' : status === 'error' ? 'report_problem' : 'hourglass_top';

  const iconColor =
    status === 'success'
      ? 'text-success-500'
      : status === 'error'
      ? 'text-error-500'
      : 'text-primary-400';

  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 text-gray-100">
      <div className="w-full max-w-xl rounded-lg border border-gray-800 bg-container-500 p-8 shadow-lg">
        <div className="flex items-center gap-3">
          <span className={`material-icons-outlined text-4xl ${iconColor}`}>{icon}</span>
          <h1 className="text-2xl font-semibold text-gray-100">{heading}</h1>
        </div>
        <p className="mt-4 text-gray-400">{message}</p>
        {refreshWarning ? (
          <p className="mt-3 rounded border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm text-yellow-300">
            {refreshWarning}
          </p>
        ) : null}

        {status === 'processing' ? (
          <div className="mt-8 flex items-center gap-3 text-sm text-gray-400">
            <span className="material-icons-outlined animate-spin">autorenew</span>
            Finishing up… please keep this tab open.
          </div>
        ) : null}

        {status === 'success' ? (
          <div className="mt-8 space-y-4">
            <div className="rounded border border-success-500/30 bg-success-500/10 px-4 py-3 text-sm text-success-400">
              Thank you! The {planName} plan is active on the selected devices. You can see each
              device&apos;s plan and renewal date on the Devices page.
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                name="Go to Dashboard"
                theme="primary"
                size="md"
                onClick={() => navigate('/')}
              />
              <Button
                name="View Devices"
                theme="secondary-color"
                size="md"
                onClick={() => navigate('/settings/device-setup?tab=devices')}
              />
            </div>
          </div>
        ) : null}

        {status === 'error' ? (
          <div className="mt-8 space-y-4">
            <div className="rounded border border-error-500/30 bg-error-500/10 px-4 py-3 text-sm text-error-400">
              If you reached this page after paying, please contact our support team with your order
              details.
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                name="Try Again"
                theme="primary"
                size="md"
                onClick={() => navigate('/pricing')}
              />
              <Button
                name="Contact Support"
                theme="secondary-color"
                size="md"
                onClick={() => navigate('/support')}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
